import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL, LOCAL_API_URL } from './Config';
import './DeployPage.css'; // Import modal styles

function ProjectVideosPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState('all_videos');
  const [deleteLanguage, setDeleteLanguage] = useState('original');
  const [deleteClipIds, setDeleteClipIds] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  
  // Detect if running locally
  const isLocalMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  const languageNames = {
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'zh': 'Chinese (Simplified)',
    'ja': 'Japanese',
    'ar': 'Arabic',
    'hi': 'Hindi',
    'pt': 'Portuguese',
    'ru': 'Russian',
    'it': 'Italian',
    'ko': 'Korean',
    'nl': 'Dutch',
    'sv': 'Swedish',
    'tr': 'Turkish',
    'ta': 'Tamil',
    'te': 'Telugu',
    'id': 'Indonesian'
  };

  useEffect(() => {
    fetchProjectAndVideos();
  }, [projectId]);

  const fetchProjectAndVideos = async () => {
    try {
      setIsLoading(true);
      
      // Use local backend API when in local mode, live API otherwise
      const currentApiUrl = isLocalMode ? LOCAL_API_URL : API_URL;
      
      if (isLocalMode) {
        // In local mode, create a mock project to avoid database dependency
        setProject({
          id: projectId,
          title: `Project ${projectId}`,
          description: 'Local development project'
        });
        
        // Fetch videos for this project from local backend
        try {
          const videosResponse = await fetch(`${currentApiUrl}/projects/${projectId}/videos`);
          if (!videosResponse.ok) {
            if (videosResponse.status === 404) {
              setVideos([]);
            } else {
              throw new Error(`Failed to fetch videos: ${videosResponse.status}`);
            }
          } else {
            const videosData = await videosResponse.json();
            setVideos(videosData);
          }
        } catch (videoErr) {
          console.warn('Failed to fetch videos from local backend, setting empty list:', videoErr);
          setVideos([]);
        }
      } else {
        // Live mode - fetch from API as usual
        // Fetch project details
        const projectResponse = await fetch(`${currentApiUrl}/projects/${projectId}`);
        if (!projectResponse.ok) {
          throw new Error(`Failed to fetch project: ${projectResponse.status}`);
        }
        const projectData = await projectResponse.json();
        setProject(projectData);

        // Fetch videos for this project
        const videosResponse = await fetch(`${currentApiUrl}/projects/${projectId}/videos`);
        if (!videosResponse.ok) {
          if (videosResponse.status === 404) {
            // No videos found, that's okay
            setVideos([]);
          } else {
            throw new Error(`Failed to fetch videos: ${videosResponse.status}`);
          }
        } else {
          const videosData = await videosResponse.json();
          setVideos(videosData);
        }
      }
    } catch (err) {
      console.error('Error fetching project and videos:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadVideo = async (videoPath, filename) => {
    try {
      const currentApiUrl = isLocalMode ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}${videoPath}`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error('Failed to download video');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading video:', err);
      alert('Failed to download video. Please try again.');
    }
  };

  const handleDeleteVideo = async (videoPath, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const currentApiUrl = isLocalMode ? LOCAL_API_URL : API_URL;
      const response = await fetch(`${currentApiUrl}/projects/${projectId}/videos`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ video_path: videoPath }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete video');
      }

      // Refresh the videos list
      await fetchProjectAndVideos();
      alert('Video deleted successfully');
    } catch (err) {
      console.error('Error deleting video:', err);
      alert('Failed to delete video. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileName = (path) => {
    return path.split('/').pop();
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const handleSyncMedia = async () => {
    if (!window.confirm('Sync all videos for this project to the live server? This may take some time for large files.')) {
      return;
    }

    setIsSyncing(true);
    setSyncResults(null);

    try {
      const currentApiUrl = isLocalMode ? LOCAL_API_URL : API_URL;

      const response = await fetch(`${currentApiUrl}/projects/${projectId}/sync-media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force_upload: false })
      });

      if (response.ok) {
        const result = await response.json();
        setSyncResults(result);

        // Handle new HTTP upload response format
        if (result.success) {
          alert(
            `Media sync completed successfully!\n` +
            `Files uploaded: ${result.successful_uploads || 0}/${result.total_files || 0}\n` +
            `${result.message || 'Upload completed'}`
          );
        } else {
          alert(
            `Media sync failed!\n` +
            `Error: ${result.error || 'Unknown error'}\n` +
            `Failed files: ${result.failed_files ? result.failed_files.length : 0}`
          );
        }
      } else {
        const errorText = await response.text();
        alert(`Failed to sync media: ${errorText}`);
      }
    } catch (error) {
      console.error('Error syncing media:', error);
      alert(`Error syncing media: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleBulkDeleteVideos = async () => {
    setIsDeleting(true);

    try {
      const currentApiUrl = isLocalMode ? LOCAL_API_URL : API_URL;

      let requestBody = {
        type: deleteType
      };

      if (deleteType === 'selective_clips') {
        // Parse comma-separated clip IDs
        const clipIds = deleteClipIds.split(',').map(id => id.trim()).filter(id => id);
        if (clipIds.length === 0) {
          alert('Please enter valid clip IDs');
          return;
        }
        requestBody.language = deleteLanguage;
        requestBody.clip_ids = clipIds;
      }

      const response = await fetch(`${currentApiUrl}/projects/${projectId}/videos/bulk`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Deletion completed!\nDeleted: ${result.deleted_count} files\nFailed: ${result.failed_count} files`);

        // Refresh video list
        await fetchProjectAndVideos();
        setShowDeleteModal(false);

        // Reset form
        setDeleteType('all_videos');
        setDeleteLanguage('original');
        setDeleteClipIds('');
      } else {
        const errorText = await response.text();
        alert(`Failed to delete videos: ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting videos:', error);
      alert(`Error deleting videos: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };



  if (isLoading) {
    return (
      <div className="project-videos-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-videos-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="back-button">
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-videos-page">
      <header className="project-videos-header">
        <div className="header-content">
          <h1>Videos for "{project?.title}"</h1>
          <p className="project-description">{project?.description}</p>
        </div>
        <div className="header-actions">
          <button onClick={() => navigate('/')} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Projects
          </button>
          {isLocalMode && (
            <button
              onClick={handleSyncMedia}
              className="sync-media-button"
              title="Sync videos to live server"
              disabled={isSyncing}
              style={{
                backgroundColor: isSyncing ? '#6c757d' : '#28a745',
                border: 'none',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: isSyncing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isSyncing ? 0.6 : 1
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
              {isSyncing ? 'Syncing...' : 'Sync Media'}
            </button>
          )}
          <button
            onClick={() => setShowDeleteModal(true)}
            className="delete-videos-button"
            title="Delete project videos"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            Delete Videos
          </button>
          <button onClick={() => navigate(`/project/${projectId}`)} className="edit-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
            Edit Project
          </button>
        </div>
      </header>

      <main className="project-videos-main">
        {videos.length === 0 ? (
          <div className="no-videos-container">
            <div className="no-videos-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
              </svg>
            </div>
            <h2>No Videos Found</h2>
            <p>This project doesn't have any generated videos yet.</p>
            <button 
              onClick={() => navigate(`/project/${projectId}`)} 
              className="create-video-button"
            >
              Go to Editor to Create Videos
            </button>
          </div>
        ) : (
          <div className="videos-grid">
            {videos.map((video, index) => (
              <div key={index} className="video-card">
                <div className="video-info">
                  <div className="video-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <div className="video-details">
                    <h3 className="video-name">{getFileName(video.path)}</h3>
                    <p className="video-metadata">
                      <span className="video-size">{formatFileSize(video.size)}</span>
                      {video.created_at && (
                        <>
                          <span className="separator">•</span>
                          <span className="video-date">{formatDate(video.created_at)}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="video-actions">
                  <button
                    onClick={() => handleDownloadVideo(video.path, getFileName(video.path))}
                    className="download-button"
                    title="Download video"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                    </svg>
                    Download
                  </button>
                  <button
                    onClick={() => handleDeleteVideo(video.path, getFileName(video.path))}
                    className="delete-button"
                    title="Delete video"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete Videos Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Delete Videos</h3>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                ×
              </button>
            </div>
            <div className="modal-content" style={{
              padding: '20px',
              maxHeight: '400px',
              overflowY: 'auto',
              width: '100%',
              boxShadow: 'none'
            }}>
              <div className="form-group">
                <label>Deletion Type</label>
                <select
                  value={deleteType}
                  onChange={(e) => setDeleteType(e.target.value)}
                  disabled={isDeleting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontFamily: 'inherit',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all_videos">Delete All Videos</option>
                  <option value="final_files">Delete Final Files Only</option>
                  <option value="selective_clips">Delete Selective Clips</option>
                </select>
              </div>

              {deleteType === 'all_videos' && (
                <div style={{
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: '8px',
                  padding: '16px',
                  margin: '16px 0'
                }}>
                  <p><strong>This will delete:</strong></p>
                  <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
                    <li>All individual clips (e.g., 812.mp4, hi_813.mp4)</li>
                    <li>All final composite videos (e.g., project_24_final.mp4)</li>
                    <li>All final composite audio (e.g., project_24_final.mp3)</li>
                  </ul>
                  <p style={{ color: '#e53e3e', fontWeight: 'bold', margin: '12px 0 0 0' }}>⚠️ This action cannot be undone!</p>
                </div>
              )}

              {deleteType === 'final_files' && (
                <div style={{
                  backgroundColor: '#fffaf0',
                  border: '1px solid #fed7aa',
                  borderRadius: '8px',
                  padding: '16px',
                  margin: '16px 0'
                }}>
                  <p><strong>This will delete:</strong></p>
                  <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
                    <li>Final composite videos (e.g., project_24_final.mp4, project_31_final_te.mp4)</li>
                    <li>Final composite audio (e.g., project_24_final.mp3)</li>
                  </ul>
                  <p style={{ color: '#38a169', margin: '8px 0' }}>Individual clips will be preserved.</p>
                  <p style={{ color: '#e53e3e', fontWeight: 'bold', margin: '12px 0 0 0' }}>⚠️ This action cannot be undone!</p>
                </div>
              )}

              {deleteType === 'selective_clips' && (
                <div className="selective-deletion">
                  <div className="form-group">
                    <label>Language</label>
                    <select
                      value={deleteLanguage}
                      onChange={(e) => setDeleteLanguage(e.target.value)}
                      disabled={isDeleting}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        backgroundColor: 'white',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="original">English (Original)</option>
                      {Object.entries(languageNames).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Clip IDs (comma-separated)</label>
                    <input
                      type="text"
                      value={deleteClipIds}
                      onChange={(e) => setDeleteClipIds(e.target.value)}
                      placeholder="e.g., 812,813,814"
                      disabled={isDeleting}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box'
                      }}
                    />
                    <small>Enter clip IDs separated by commas. For English, clips are named like "812.mp4". For other languages, they're named like "hi_813.mp4".</small>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="modal-delete"
                onClick={handleBulkDeleteVideos}
                disabled={isDeleting || (deleteType === 'selective_clips' && !deleteClipIds.trim())}
                style={{
                  backgroundColor: '#dc3545',
                  border: 'none',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  opacity: isDeleting ? 0.6 : 1
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Videos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectVideosPage;